/**
 * UranIA PA — Pagina creazione nuova skill (admin).
 *
 * Route ``/admin/skills/new`` — wrapper sopra ``SkillVersionForm`` in
 * modalita' ``create``. RBAC guard UX uguale alla list page
 * (``/admin/skills``): solo ``platform_admin`` vede il form, gli altri
 * vedono il fallback ``forbidden``. L'autorizzazione reale e' server-side
 * (skills-registry ruolo realm + step-up MFA in prod).
 */

import { useTranslation } from 'react-i18next';
import { createRoute, Link, useRouter } from '@tanstack/react-router';
import type { JSX } from 'react';

import { SkillVersionForm } from '../../../components/admin/SkillVersionForm.tsx';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsPlatformAdmin } from '../../../lib/auth/rbac.ts';
import { Route as RootRoute } from '../../__root.tsx';

export function AdminSkillNewPage(): JSX.Element {
  const { t } = useTranslation();
  const router = useRouter();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isPlatformAdmin = principalIsPlatformAdmin(principal);

  if (!isAuthenticated) {
    return (
      <section className="urania-admin-skill-new" data-testid="admin-skill-new-unauthenticated">
        <h1 className="display-5 mb-3">{t('admin.skills.new.title')}</h1>
        <div className="alert alert-info" role="region">
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link to="/login" className="btn btn-primary">
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <section className="urania-admin-skill-new" data-testid="admin-skill-new-forbidden">
        <h1 className="display-5 mb-3">{t('admin.skills.new.title')}</h1>
        <div className="alert alert-warning" role="alert">
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-admin-skill-new" data-testid="admin-skill-new-page">
      <nav className="mb-3 d-flex gap-3 flex-wrap small">
        <Link to="/admin" className="text-decoration-none">
          ← {t('admin.console.title')}
        </Link>
        <span className="text-muted">·</span>
        <Link to="/admin/skills" className="text-decoration-none" data-testid="admin-skill-new-back">
          {t('admin.skills.detail.back_to_list')}
        </Link>
      </nav>

      <div
        className="rounded p-4 mb-4 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="position-relative" style={{ zIndex: 1, maxWidth: 760 }}>
          <span
            className="badge mb-2"
            style={{
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: '0.7rem',
              letterSpacing: '0.05em',
            }}
          >
            ADMIN · NUOVA SKILL AI
          </span>
          <h1 className="h3 mb-2">{t('admin.skills.new.title')}</h1>
          <p className="mb-0 opacity-90 small">{t('admin.skills.new.subtitle')}</p>
        </div>
        <svg
          aria-hidden="true"
          className="position-absolute d-none d-lg-block"
          style={{ top: '-30px', right: '-30px', opacity: 0.12 }}
          width="200"
          height="200"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M5 0a.5.5 0 0 1 .5.5V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2A2.5 2.5 0 0 1 14 4.5h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14a2.5 2.5 0 0 1-2.5 2.5v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14A2.5 2.5 0 0 1 2 11.5H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2A2.5 2.5 0 0 1 4.5 2V.5A.5.5 0 0 1 5 0z" />
        </svg>
      </div>

      <div className="row g-3">
        {/* Colonna form */}
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white border-bottom py-2 px-3">
              <span
                className="text-uppercase text-muted fw-semibold"
                style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
              >
                {t('admin.skills.form_header')}
              </span>
            </div>
            <div className="card-body">
              <SkillVersionForm
                mode="create"
                onSuccess={(response): void => {
                  setTimeout(() => {
                    void router.navigate({
                      to: '/admin/skills/$skillId',
                      params: { skillId: response.skill_id },
                    });
                  }, 1500);
                }}
              />
            </div>
          </div>
        </div>

        {/* Colonna info contestuale */}
        <div className="col-12 col-lg-4">
          <div
            className="card shadow-sm border-0 h-100"
            style={{ backgroundColor: '#f0f9ff' }}
          >
            <div className="card-body">
              <h2 className="h6 mb-3" style={{ color: '#084298' }}>
                {t('admin.skills.info.title')}
              </h2>
              <ul className="ps-3 small mb-3" style={{ color: '#084298' }}>
                <li className="mb-2">{t('admin.skills.info.point_encrypted')}</li>
                <li className="mb-2">{t('admin.skills.info.point_versioning')}</li>
                <li className="mb-2">{t('admin.skills.info.point_scope')}</li>
                <li className="mb-2">{t('admin.skills.info.point_audit')}</li>
                <li className="mb-0">{t('admin.skills.info.point_router')}</li>
              </ul>
              <div className="border-top pt-3 mt-3">
                <p
                  className="small fw-semibold mb-1"
                  style={{ color: '#084298' }}
                >
                  {t('admin.skills.info.stack_title')}
                </p>
                <ul
                  className="list-unstyled small mb-0"
                  style={{ color: '#084298' }}
                >
                  <li><code>AES-256-GCM</code> · envelope</li>
                  <li><code>Vault Transit</code> · KEK per tenant</li>
                  <li><code>pgcrypto</code> · server-side cifratura</li>
                  <li><code>semver</code> · 1.0.0, 1.0.1-rc1, ...</li>
                  <li><code>audit_events</code> · hash chain</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/admin/skills/new',
  component: AdminSkillNewPage,
});
